/**
 * Pure tree-manipulation helpers for the FK Breadcrumb navigation tree.
 *
 * Slice 3 uses stack-shaped trees only (each node has 0 or 1 child). Slice 4
 * adds branching (sibling helpers). All helpers are pure: they take a tree
 * (or pieces of one) and return a new tree without mutating the input.
 *
 * Serialization helpers + the Slice 1 → tree migrator live in
 * `breadcrumbTreeSerialize.ts` and are re-exported below so callers can keep
 * importing everything from `./breadcrumbTree`.
 */

import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
  RowNode,
  TableNode,
} from '../../../core/types/workspaceTypes';

let idCounter = 0;
function freshId(prefix: string): NavigationNodeId {
  // Prefer `crypto.randomUUID()` when available — eliminates the
  // module-level counter race in tests that import this file across
  // multiple Vitest workers / contexts. Falls back to the counter
  // otherwise (e.g. older Node test envs without `crypto.randomUUID`).
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  }
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

/** Build a fresh table-node (root or filtered child). */
export function makeTableNode(input: Omit<TableNode, 'id' | 'kind' | 'parentId' | 'childIds' | 'isJoinCollapsed'> & {
  readonly parentId?: NavigationNodeId | null;
  readonly isJoinCollapsed?: boolean;
}): TableNode {
  return {
    kind: 'table',
    id: freshId('t'),
    parentId: input.parentId ?? null,
    childIds: [],
    schema: input.schema,
    table: input.table,
    filters: input.filters,
    reverseFkColumn: input.reverseFkColumn,
    isJoinCollapsed: input.isJoinCollapsed ?? false,
    displayLabel: input.displayLabel,
  };
}

/** Build a fresh row-node. */
export function makeRowNode(input: Omit<RowNode, 'id' | 'kind' | 'parentId' | 'childIds'> & {
  readonly parentId?: NavigationNodeId | null;
}): RowNode {
  return {
    kind: 'row',
    id: freshId('r'),
    parentId: input.parentId ?? null,
    childIds: [],
    schema: input.schema,
    table: input.table,
    pk: input.pk,
    displayLabel: input.displayLabel,
  };
}

/** Initialize a tree from a single root node. */
export function initTree(root: NavigationNode): NavigationTree {
  const rooted: NavigationNode = { ...root, parentId: null, childIds: [] } as NavigationNode;
  return {
    rootId: rooted.id,
    activeNodeId: rooted.id,
    nodes: { [rooted.id]: rooted },
  };
}

/** Look up a node by id; throws if missing. */
export function findById(tree: NavigationTree, id: NavigationNodeId): NavigationNode {
  const node = tree.nodes[id];
  if (!node) throw new Error(`NavigationTree: node ${id} not found`);
  return node;
}

/**
 * Push `child` as a child of the currently-active node and activate it.
 * Returns the new tree and the new child id.
 */
export function push(tree: NavigationTree, child: NavigationNode): { tree: NavigationTree; newId: NavigationNodeId } {
  const parent = findById(tree, tree.activeNodeId);
  const newChild: NavigationNode = { ...child, parentId: parent.id, childIds: [] } as NavigationNode;
  const newParent: NavigationNode = {
    ...parent,
    childIds: [...parent.childIds, newChild.id],
  } as NavigationNode;
  return {
    tree: {
      ...tree,
      activeNodeId: newChild.id,
      nodes: { ...tree.nodes, [parent.id]: newParent, [newChild.id]: newChild },
    },
    newId: newChild.id,
  };
}

/**
 * Activate a node (set as active). Does not mutate structure. Throws if the
 * node doesn't exist.
 */
export function activate(tree: NavigationTree, id: NavigationNodeId): NavigationTree {
  if (!tree.nodes[id]) throw new Error(`NavigationTree: cannot activate missing node ${id}`);
  return { ...tree, activeNodeId: id };
}

/**
 * Collect all descendant ids of `id` (inclusive). Stable iteration order
 * — children added in order, then their children, etc.
 */
export function collectSubtree(tree: NavigationTree, id: NavigationNodeId): NavigationNodeId[] {
  const out: NavigationNodeId[] = [];
  const queue: NavigationNodeId[] = [id];
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined) break;
    if (!tree.nodes[cur]) continue;
    out.push(cur);
    for (const c of tree.nodes[cur].childIds) queue.push(c);
  }
  return out;
}

/**
 * Dismiss a node and its subtree. If the dismissed node is on the active
 * path, the active node moves to the dismissed node's parent.
 *
 * Returns `null` if the dismissal would empty the tree (dismissing the
 * root); callers may interpret null as "clear the navigationTree on the tab".
 */
export function dismiss(tree: NavigationTree, id: NavigationNodeId): NavigationTree | null {
  const target = tree.nodes[id];
  if (!target) return tree;
  if (id === tree.rootId) {
    // Dismissing the root collapses the entire tree.
    return null;
  }
  const toRemove = new Set(collectSubtree(tree, id));
  const parentId = target.parentId;
  // Build new nodes map without the subtree, and detach the parent's childIds.
  const nextNodes: Record<NavigationNodeId, NavigationNode> = {};
  for (const [nodeId, node] of Object.entries(tree.nodes)) {
    if (toRemove.has(nodeId)) continue;
    if (nodeId === parentId) {
      nextNodes[nodeId] = {
        ...node,
        childIds: node.childIds.filter((c) => c !== id),
      } as NavigationNode;
      continue;
    }
    nextNodes[nodeId] = node;
  }
  // If the active node was inside the removed subtree, move active to parent.
  const activeWasRemoved = toRemove.has(tree.activeNodeId);
  const activeNodeId = activeWasRemoved ? (parentId ?? tree.rootId) : tree.activeNodeId;
  return { ...tree, activeNodeId, nodes: nextNodes };
}

/**
 * Path from root to the active node. Used to render the chip-row left → right.
 */
export function activePathFromRoot(tree: NavigationTree): NavigationNode[] {
  const path: NavigationNode[] = [];
  let cur: NavigationNode | undefined = tree.nodes[tree.activeNodeId];
  while (cur) {
    path.push(cur);
    if (cur.parentId === null) break;
    cur = tree.nodes[cur.parentId];
  }
  return path.reverse();
}

/** Tree depth, defined as the number of nodes on the active path. */
export function depth(tree: NavigationTree): number {
  return activePathFromRoot(tree).length;
}

// Re-export serialization + migrator and stack/branching queries so callers
// can keep importing everything from `./breadcrumbTree`.
export {
  serialize, deserialize, migrateLegacyViewMode,
  serializeForSession, deserializeFromSession, SERIALIZED_TREE_DEPTH_CAP,
} from './breadcrumbTreeSerialize';
export {
  stackChainFromRoot, stackChainLength, pushChild, getChildren, getSiblings,
  setActive, pruneSubtree, subtreeIdsOf, parentOf, firstChildOf,
  resolveKeyboardTarget,
} from './breadcrumbTreeQueries';
export type { BreadcrumbKey } from './breadcrumbTreeQueries';
